import { createElement, useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  type CenteredDropdownAlignPoints,
  type DropdownProperties,
  VERTICAL_OFFSET,
  getCenteredDropdownAlignPoints,
  usePreventDoubleClick,
} from '../../../../dropdown';
import { SLOT_DROPDOWN_MENU_MIN_WIDTH } from '../../../constants';
import { useGetZonedSlotStart } from '../../../hooks';
import { SlotButton } from '../../../slot-button';
import type { RenderTimeSlot } from '../../../types';
import styles from '../month-day-cell.module.css';

type UseMonthTimeSlotProperties = {
  day: Date;
  renderTimeSlot?: RenderTimeSlot;
  timezone: string;
};

type UseMonthTimeSlotReturn = {
  isSlotIndicatorVisible: boolean;
  renderedTimeSlot: ReactNode;
};

export const useMonthTimeSlot = ({
  day,
  renderTimeSlot,
  timezone,
}: UseMonthTimeSlotProperties): UseMonthTimeSlotReturn => {
  const [isSlotIndicatorVisible, setIsSlotIndicatorVisible] = useState(false);
  const [alignPoints, setAlignPoints] = useState<CenteredDropdownAlignPoints>(['tc', 'tc']);
  const [slotTriggerElement, setSlotTriggerElement] = useState<HTMLButtonElement | null>(null);
  const getZonedSlotStart = useGetZonedSlotStart();

  const slotStart = useMemo(() => getZonedSlotStart(day, timezone), [day, getZonedSlotStart, timezone]);

  usePreventDoubleClick(slotTriggerElement);

  const handleOpenChange: NonNullable<DropdownProperties['onOpenChange']> = useCallback(
    (nextIsOpen) => {
      if (nextIsOpen && slotTriggerElement) {
        setAlignPoints(
          getCenteredDropdownAlignPoints(slotTriggerElement.getBoundingClientRect(), SLOT_DROPDOWN_MENU_MIN_WIDTH),
        );
      }

      setIsSlotIndicatorVisible(nextIsOpen);
    },
    [slotTriggerElement],
  );

  const renderedTimeSlot = renderTimeSlot?.({
    children: createElement(SlotButton, { className: styles.slot, ref: setSlotTriggerElement }),
    dropdownProps: {
      align: {
        offset: [0, VERTICAL_OFFSET],
        overflow: { adjustX: true, adjustY: true },
        points: alignPoints,
      },
      autoAdjustOverflow: true,
      classNames: { root: styles['slot-dropdown'] },
      getPopupContainer: () => document.body,
      menu: { className: styles['slot-dropdown-menu'] },
      onOpenChange: handleOpenChange,
      positionAtClick: false,
      trigger: ['click'],
    },
    slotStart,
  });

  return useMemo(
    () => ({
      isSlotIndicatorVisible,
      renderedTimeSlot,
    }),
    [isSlotIndicatorVisible, renderedTimeSlot],
  );
};
